import {
  FieldElement,
  IconElement,
  ItemTemplate,
  MobileListItem,
  RowElement,
  TemplateElement,
  ViewElement,
} from '@mobileTypes'

/**
 * Converts Simple mode configuration (rows + icon) to Advanced mode (itemTemplate)
 */
export const convertSimpleToAdvanced = (listItem: Partial<MobileListItem>): ItemTemplate => {
  const rows = listItem.rows || []
  const icon = listItem.icon

  const children: TemplateElement[] = []

  const mainView: ViewElement = {
    type: 'view',
    style: {
      flexDirection: 'row',
      flex: 1,
      gap: 16,
    },
    children: [],
  }

  // If we have icon, create a view for icon column
  if (icon) {
    const iconView: ViewElement = {
      type: 'view',
      style: {
        flexDirection: 'column',
        justifyContent: 'center',
      },
      children: [
        {
          type: 'icon',
          isJS: icon.isJS || false,
          size: icon.size || 36,
          value: icon.value || '',
          displayRule: icon.displayRule,
          useStyleParameters: icon.useStyleParameters,
          styleParameters: icon.styleParameters,
          ...(icon.color != null && icon.color !== '' && { color: icon.color }),
        } as IconElement,
      ],
    }
    mainView.children!.push(iconView)
  }

  const fieldsView: ViewElement = {
    type: 'view',
    style: {
      flexDirection: 'column',
      flex: 1,
      gap: 4,
    },
    children: [],
  }
  // Convert row elements to field elements
  rows.forEach(row => {
    const rowView: ViewElement = {
      type: 'view',
      style: {
        flexDirection: 'row',
        flex: 0,
        gap: 4,
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
      children: [],
    }
    row.elements.forEach(element => {
      const fieldElement: FieldElement = convertRowElementToField(element)
      rowView.children!.push(fieldElement)
    })
    fieldsView.children!.push(rowView)
  })

  mainView.children!.push(fieldsView)

  const chevronView: ViewElement = {
    type: 'view',
    style: {
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 0,
    },
    children: [
      {
        type: 'icon',
        isJS: false,
        size: 28,
        // Save as JSON string like static icons in TemplateElementConfigDialog
        value: JSON.stringify({
          type: 'MaterialDesignIcons',
          value: 'chevron-right',
          size: 28,
        }),
        displayRule: undefined,
      } as IconElement,
    ],
  }
  mainView.children!.push(chevronView)

  children.push(mainView)

  return { children }
}

/**
 * Converts a RowElement to a FieldElement
 */
const convertRowElementToField = (rowElement: RowElement): FieldElement => {
  return {
    type: 'field',
    code: rowElement.code,
    order: rowElement.order,
    title: rowElement.title,
    value: rowElement.value,
    pinnedColumn: rowElement.pinnedColumn,
    objectValue: rowElement.objectValue,
    pathArray: rowElement.pathArray,
    actions: rowElement.actions,
    columnToSort: rowElement.columnToSort,
    objectCode: rowElement.objectCode,
    valueType: rowElement.valueType,
    fieldType: rowElement.fieldType,
    bindType: rowElement.bindType,
    formatDate: rowElement.formatDate,
    editable: rowElement.editable,
    enumDescription: rowElement.enumDescription,
    visibility: rowElement.visibility,
    useLocalParameters: rowElement.useLocalParameters,
    useDisplayRule: rowElement.useDisplayRule,
    isPk: rowElement.isPk,
    parameters: rowElement.parameters,
    restrictions: rowElement.restrictions,
    valueFormat: rowElement.valueFormat,
    displayRule: rowElement.displayRule,
    preFill: rowElement.preFill,
    hideWhenEmpty: rowElement.hideWhenEmpty,
    useStyleParameters: rowElement.useStyleParameters,
    styleParameters: rowElement.styleParameters,
  } as FieldElement
}
